import type { IntlShape } from 'react-intl';
import type { PatientsListQueryParams } from '~/backend/connector-next-api/connector-next-components';
import {
    COURSE_STAGE_VALUES,
    courseStageLabelId,
    type CourseStage,
} from './patientListModel';
import type { CatalogMessageId } from '~/i18n';

/**
 * Wire value unions of the three filter query parameters, from the generated
 * client. The connector spec declares the enum vocabularies, so a backend
 * enum change regenerates these unions and fails compilation at the option
 * tables below instead of silently leaving the menu stale.
 */
type StatusWire = NonNullable<PatientsListQueryParams['status']>[number];
type CourseStageWire = NonNullable<
    PatientsListQueryParams['courseStage']
>[number];
type ModalityWire = NonNullable<PatientsListQueryParams['modality']>[number];

/**
 * The multi-valued filter dimensions; empty array means unfiltered. Values
 * are wire-typed, so state can only hold what the connector accepts.
 */
type PatientFilterFacets = {
    /** The stage the user has put the patient's course at. */
    courseStage: readonly CourseStageWire[];
    /** Patient lifecycle status (Active / Archived). */
    status: readonly StatusWire[];
    /** Image modality the patient holds at least one series of. */
    modality: readonly ModalityWire[];
};

/** Every filter dimension. Evaluated server-side so the reported total matches. */
export type PatientFilters = PatientFilterFacets & {
    /** Display name the patient must be assigned to; undefined for any assignee. */
    assignedTo: string | undefined;
};

export type PatientFilterFacetId = keyof PatientFilterFacets;

export const NO_PATIENT_FILTERS: PatientFilters = {
    courseStage: [],
    status: [],
    modality: [],
    assignedTo: undefined,
};

export type PatientFilterOption<V extends string = string> = {
    /** Connector enum member name, sent verbatim. */
    value: V;
    /**
     * Option label message id, or undefined to show the wire value as-is
     * (matches the row chips for modality).
     */
    labelId?: CatalogMessageId;
};

export type PatientFilterFacet = {
    id: PatientFilterFacetId;
    labelId: CatalogMessageId;
    /**
     * "{count} statuses" - the chip's value segment once more than one is
     * selected.
     */
    countLabelId: CatalogMessageId;
    options: readonly PatientFilterOption[];
};

/**
 * Label id per lifecycle status. Keyed by the wire union, so every connector
 * value must be handled here by name.
 */
const PATIENT_STATUS_LABELS = {
    Active: 'patientList.filter.status.active',
    Archived: 'patientList.filter.status.archived',
} satisfies Record<StatusWire, CatalogMessageId>;

const PATIENT_STATUS_OPTIONS: readonly PatientFilterOption<StatusWire>[] = (
    Object.keys(PATIENT_STATUS_LABELS) as StatusWire[]
).map((value) => ({ value, labelId: PATIENT_STATUS_LABELS[value] }));

/**
 * Label id (or none: the code labels itself, matching the row chips) per
 * image modality, in menu order with the unparsed-modality value (Unknown, a
 * real stored value) last. Keyed by the wire union; `PET` is the connector's
 * name for DICOM's `PT`.
 */
const MODALITY_LABELS = {
    CT: undefined,
    MR: undefined,
    PET: undefined,
    CBCT: undefined,
    Unknown: 'patientList.filter.modality.unknown',
} satisfies Record<ModalityWire, CatalogMessageId | undefined>;

const MODALITY_OPTIONS: readonly PatientFilterOption<ModalityWire>[] = (
    Object.keys(MODALITY_LABELS) as ModalityWire[]
).map((value) => ({ value, labelId: MODALITY_LABELS[value] }));

const COURSE_STAGE_OPTIONS: readonly PatientFilterOption<CourseStageWire>[] =
    COURSE_STAGE_VALUES.map((value) => ({
        value,
        labelId: courseStageLabelId(value),
    }));

/**
 * Every filter dimension. Course-stage options come from the same table that
 * orders the course-stage strip, so filter and display can't diverge.
 */
export const PATIENT_FILTER_FACETS: readonly PatientFilterFacet[] = [
    {
        id: 'courseStage',
        labelId: 'patientList.filter.facet.courseStage',
        countLabelId: 'patientList.filter.count.courseStage',
        options: COURSE_STAGE_OPTIONS,
    },
    {
        id: 'status',
        labelId: 'patientList.filter.facet.status',
        countLabelId: 'patientList.filter.count.status',
        options: PATIENT_STATUS_OPTIONS,
    },
    {
        id: 'modality',
        labelId: 'patientList.filter.facet.modality',
        countLabelId: 'patientList.filter.count.modality',
        options: MODALITY_OPTIONS,
    },
];

/**
 * The dimensions the filter menu offers. Course stage is not among them: it
 * is filtered from the always-visible stage strip, one stage at a time, so
 * the same dimension is never presented by two controls at once.
 */
export const PATIENT_FILTER_MENU_FACETS: readonly PatientFilterFacet[] =
    PATIENT_FILTER_FACETS.filter((facet) => facet.id !== 'courseStage');

/**
 * The course stage the strip shows pressed: the one stage filtered on, or
 * undefined for "all" (no course-stage filter, or several stages at once,
 * which no single toggle stands for).
 */
export function selectedCourseStage(
    filters: PatientFilters
): CourseStage | undefined {
    return filters.courseStage.length === 1
        ? filters.courseStage[0]
        : undefined;
}

/**
 * `filters` narrowed to `stage`, or with the course-stage dimension cleared
 * when `stage` is undefined; the other dimensions are untouched.
 */
export function withCourseStage(
    filters: PatientFilters,
    stage: CourseStage | undefined
): PatientFilters {
    return withFacetSelection(
        filters,
        'courseStage',
        new Set(stage === undefined ? [] : [stage])
    );
}

/**
 * Display label of one option: its message, or the wire value when it is
 * its own label.
 */
export function filterOptionLabel(
    intl: IntlShape,
    option: PatientFilterOption
): string {
    return option.labelId === undefined
        ? option.value
        : intl.formatMessage({ id: option.labelId });
}

/** The facet's selected values, widened for lookups. */
function selectedValues(
    filters: PatientFilters,
    facetId: PatientFilterFacetId
): readonly string[] {
    return filters[facetId];
}

/** Whether `value` is currently selected in `facetId`. */
export function isFilterSelected(
    filters: PatientFilters,
    facetId: PatientFilterFacetId,
    value: string
): boolean {
    return selectedValues(filters, facetId).includes(value);
}

/**
 * One facet's typed option values that `selected` names, in table order.
 * Rebuilding from the table is what keeps the state wire-valid: a value the
 * facet does not offer cannot survive into it.
 */
function selectFromTable<V extends string>(
    options: readonly PatientFilterOption<V>[],
    selected: ReadonlySet<string>
): readonly V[] {
    return options
        .map((option) => option.value)
        .filter((value) => selected.has(value));
}

/** `filters` with `facetId` holding exactly the values in `selected`. */
function withFacetSelection(
    filters: PatientFilters,
    facetId: PatientFilterFacetId,
    selected: ReadonlySet<string>
): PatientFilters {
    return {
        courseStage:
            facetId === 'courseStage'
                ? selectFromTable(COURSE_STAGE_OPTIONS, selected)
                : filters.courseStage,
        status:
            facetId === 'status'
                ? selectFromTable(PATIENT_STATUS_OPTIONS, selected)
                : filters.status,
        modality:
            facetId === 'modality'
                ? selectFromTable(MODALITY_OPTIONS, selected)
                : filters.modality,
        assignedTo: filters.assignedTo,
    };
}

/** `filters` narrowed to the patients assigned to `name`, or to any assignee when undefined. */
export function withAssignedTo(
    filters: PatientFilters,
    name: string | undefined
): PatientFilters {
    return { ...filters, assignedTo: name };
}

/**
 * `filters` with `value` toggled in `facetId`; result keeps the facet's
 * declared order, not selection order.
 */
export function toggleFilterValue(
    filters: PatientFilters,
    facetId: PatientFilterFacetId,
    value: string
): PatientFilters {
    const selected = new Set<string>(selectedValues(filters, facetId));
    if (!selected.delete(value)) {
        selected.add(value);
    }
    return withFacetSelection(filters, facetId, selected);
}

/** Whether any dimension is filtered. */
export function hasActivePatientFilters(filters: PatientFilters): boolean {
    return (
        filters.assignedTo !== undefined ||
        PATIENT_FILTER_FACETS.some((facet) => filters[facet.id].length > 0)
    );
}

/** Whether any of the menu's dimensions is filtered; the strip and the assignee toggle show their own state. */
export function hasActiveMenuFilters(filters: PatientFilters): boolean {
    return PATIENT_FILTER_MENU_FACETS.some(
        (facet) => filters[facet.id].length > 0
    );
}

/** `filters` with `facetId` emptied; the other dimensions are untouched. */
export function clearFacet(
    filters: PatientFilters,
    facetId: PatientFilterFacetId
): PatientFilters {
    return withFacetSelection(filters, facetId, new Set());
}

export type ActiveFilterChip = {
    facetId: PatientFilterFacetId;
    /** Facet name, for the chip's leading segment ("Modality"). */
    facetLabel: string;
    /** The selected values' labels, in facet order. Never empty. */
    valueLabels: string[];
    /**
     * Chip value text: the single label, or a count ("3 statuses") once
     * more than one is selected; full list stays in `valueLabels` for the
     * title.
     */
    valueSummary: string;
};

/**
 * One chip per filtered menu dimension, in facet order; keeps filters visible
 * so the footer's matching-count isn't mistaken for the whole cohort. The
 * course-stage dimension is left out: its strip is always on screen and
 * shows the selected stage itself.
 */
export function activeFilterChips(
    intl: IntlShape,
    filters: PatientFilters
): ActiveFilterChip[] {
    return PATIENT_FILTER_MENU_FACETS.filter(
        (facet) => filters[facet.id].length > 0
    ).map((facet) => {
        const selected = selectedValues(filters, facet.id);
        const valueLabels = facet.options
            .filter((option) => selected.includes(option.value))
            .map((option) => filterOptionLabel(intl, option));
        return {
            facetId: facet.id,
            facetLabel: intl.formatMessage({ id: facet.labelId }),
            valueLabels,
            valueSummary:
                valueLabels.length === 1
                    ? valueLabels[0]
                    : intl.formatMessage(
                          { id: facet.countLabelId },
                          { count: valueLabels.length }
                      ),
        };
    });
}

type FilterQueryParamKey = PatientFilterFacetId | 'assignedTo';

/**
 * Connector query params for `filters`; an unselected dimension is
 * omitted, not sent empty, since the connector reads absent as unfiltered
 * and empty as a parse error. The Required source object makes adding a
 * dimension a compile error here until it is sent.
 */
export function patientFilterQueryParams(
    filters: PatientFilters
): Partial<Pick<PatientsListQueryParams, FilterQueryParamKey>> {
    const all: Required<Pick<PatientsListQueryParams, FilterQueryParamKey>> = {
        courseStage: [...filters.courseStage],
        status: [...filters.status],
        modality: [...filters.modality],
        assignedTo: filters.assignedTo ?? '',
    };
    const params: Partial<typeof all> = {};
    if (all.courseStage.length > 0) {
        params.courseStage = all.courseStage;
    }
    if (all.status.length > 0) {
        params.status = all.status;
    }
    if (all.modality.length > 0) {
        params.modality = all.modality;
    }
    if (all.assignedTo !== '') {
        params.assignedTo = all.assignedTo;
    }
    return params;
}
